import { takeLatest, call, put, select } from 'redux-saga/effects';
import { serializeParams } from 'utils/functions/general';
import { addToast } from 'modules/common/commonSlice';
import { globalCountryId } from 'constants/general';
import {
  getPriceList,
  createPriceList,
  editPriceList,
  getProviders,
  getDoctorProfile,
  searchDoctors,
  removeProvider,
  getDropdowns,
  branches,
  editProvider,
  searchPharmacies,
  addProvidersBulk,
  getServices,
  removeService,
  addService,
  getMedicalServices,
  searchPharmacyProducts,
  getAreas,
  searchEntities,
  entityProviders,
  addEntityProvider,
  editEntityProvider,
  deleteEntityProvider,
} from './api';
import {
  fetchPriceListsSuccess,
  fetchPriceListsFail,
  fetchRelatedPriceListsSuccess,
  fetchRelatedPriceListsFail,
  editPriceListSuccess,
  editPriceListFail,
  submitPriceListSuccess,
  submitPriceListFail,
  fetchProvidersSuccess,
  fetchProvidersFail,
  fetchDoctorsListSuccess,
  fetchDoctorsListFail,
  fetchDoctorProfileSuccess,
  fetchDoctorProfileFail,
  removeProviderSuccess,
  removeProviderFail,
  submitProvidersBulkSuccess,
  submitProvidersBulkFail,
  fetchDropdownsSuccess,
  fetchDropdownsFail,
  fetchBranchesSuccess,
  fetchBranchesFail,
  editProviderSuccess,
  editProviderFail,
  fetchPharmaciesListSuccess,
  fetchPharmaciesListFail,
  fetchServicesListSuccess,
  fetchServicesListFail,
  removeServiceSuccess,
  removeServiceFail,
  addServiceSuccess,
  addServiceFail,
  fetchMedicalServicesSuccess,
  fetchMedicalServicesFail,
  fetchPharmacyProductsSuccess,
  fetchPharmacyProductsFail,
  fetchEntitiesSuccess,
  fetchEntitiesFail,
  fetchEntityProvidersSuccess,
  fetchEntityProvidersFail,
  submitEntityProviderSuccess,
  submitEntityProviderFail,
  editEntityProviderSuccess,
  editEntityProviderFail,
  removeEntityProviderSuccess,
  removeEntityProviderFail,
} from './categoriesSlice';

function* fetchPriceListsSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const response = yield call(getPriceList, queryStringOfObj);
    yield put(fetchPriceListsSuccess(response.data.data));
  } catch (error) {
    yield put(fetchPriceListsFail(error?.message));
  }
}

function* fetchRelatedPriceListsSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const response = yield call(getPriceList, queryStringOfObj);
    yield put(fetchRelatedPriceListsSuccess(response.data.data));
  } catch (error) {
    yield put(fetchRelatedPriceListsFail(error?.message));
  }
}

function* submitPriceListSaga({ payload }) {
  try {
    yield call(createPriceList, payload);
    yield put(submitPriceListSuccess());
    yield put(addToast({ type: 'success', message: 'Price list is added successfully' }));
  } catch (error) {
    yield put(submitPriceListFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while adding Price list' }));
  }
}

function* editPriceListSaga({ payload }) {
  try {
    yield call(editPriceList, payload);
    yield put(editPriceListSuccess());
    yield put(addToast({ type: 'success', message: 'Price list is edited successfully' }));
  } catch (error) {
    yield put(editPriceListFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while editing Price list' }));
  }
}

function* fetchProvidersSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const response = yield call(getProviders, queryStringOfObj);
    yield put(fetchProvidersSuccess(response.data.data));
  } catch (error) {
    yield put(fetchProvidersFail(error?.message));
  }
}

function* fetchBranchesSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams({ ...payload, IncludeNoShowPricesEntities: true });
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );
    const response = yield call(branches, queryStringOfObj, healthPlanCountryId);
    yield put(fetchBranchesSuccess(response?.data?.Data?.Results));
  } catch (error) {
    yield put(fetchBranchesFail(error?.message));
  }
}

function* fetchDoctorsSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams({ ...payload, includeExclusive: true });
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );
    const response = yield call(searchDoctors, queryStringOfObj, healthPlanCountryId);
    yield put(fetchDoctorsListSuccess(response.data.Result));
  } catch (error) {
    yield put(fetchDoctorsListFail(error?.message));
  }
}

function* fetchDoctorProfileSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const response = yield call(getDoctorProfile, queryStringOfObj);
    yield put(fetchDoctorProfileSuccess(response.data.data));
  } catch (error) {
    yield put(fetchDoctorProfileFail(error?.message));
  }
}

function* addProvidersBulkSaga({ payload }) {
  try {
    yield call(addProvidersBulk, payload);
    yield put(submitProvidersBulkSuccess());
    yield put(addToast({ type: 'success', message: 'Providers are added successfully' }));
  } catch (error) {
    yield put(submitProvidersBulkFail(error?.message));
    yield put(
      addToast({
        type: 'error',
        message: `An error occured while adding Providers: ${error?.response?.data}`,
      }),
    );
  }
}

function* removeProviderSuccessSaga({ payload }) {
  try {
    yield call(removeProvider, payload);
    yield put(removeProviderSuccess());
    yield put(addToast({ type: 'success', message: 'Provider is removed successfully' }));
  } catch (error) {
    yield put(removeProviderFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while removing Provider' }));
  }
}

function* editProviderSaga({ payload }) {
  try {
    yield call(editProvider, payload);
    yield put(editProviderSuccess());
    yield put(addToast({ type: 'success', message: 'Provider is edited successfully' }));
  } catch (error) {
    yield put(editProviderFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while editing Provider' }));
  }
}

function* fetchPharmaciesSaga({ payload }) {
  try {
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );
    const response = yield call(searchPharmacies, payload, healthPlanCountryId);
    yield put(fetchPharmaciesListSuccess(response.data.results));
  } catch (error) {
    yield put(fetchPharmaciesListFail(error?.message));
  }
}

function* fetchDropdownsSaga() {
  try {
    const dropDownResponse = yield call(getDropdowns);
    const areasResponse = yield call(getAreas);
    const SelectedHealthPlanCountryId = yield select(
      state => state.healthPlans.selectedHealthPlan.data.countryId,
    );
    const areas = [];
    areasResponse?.data?.Data?.forEach(area => {
      if (
        area.CountryId === SelectedHealthPlanCountryId ||
        SelectedHealthPlanCountryId === globalCountryId
      ) {
        areas.push({
          key: area?.AreaKey,
          name: area?.LanguageItemModels?.find(model => model.LanguageId === 1)?.Name,
        });
      }
    });
    const specialities = dropDownResponse.data.Specialties.map(({ Key, Name }) => ({
      key: Key,
      name: Name,
    }));
    yield put(fetchDropdownsSuccess({ areas, specialities }));
  } catch (error) {
    yield put(fetchDropdownsFail(error?.message));
  }
}

function* fetchServicesListSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const response = yield call(getServices, queryStringOfObj);
    yield put(fetchServicesListSuccess(response.data.data));
  } catch (error) {
    yield put(fetchServicesListFail(error?.message));
  }
}

function* addServiceSaga({ payload }) {
  try {
    yield call(addService, payload);
    yield put(addServiceSuccess());
    yield put(addToast({ type: 'success', message: 'Service is added successfully' }));
  } catch (error) {
    yield put(addServiceFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while adding Service' }));
  }
}

function* removeServiceSaga({ payload }) {
  try {
    yield call(removeService, payload);
    yield put(removeServiceSuccess());
    yield put(addToast({ type: 'success', message: 'Service is removed successfully' }));
  } catch (error) {
    yield put(removeServiceFail(error?.message));
    yield put(addToast({ type: 'error', message: 'An error occured while removing Service' }));
  }
}

function* fetchMedicalServicesSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );
    const response = yield call(getMedicalServices, queryStringOfObj, healthPlanCountryId);
    yield put(fetchMedicalServicesSuccess(response.data.data.results));
  } catch (error) {
    yield put(fetchMedicalServicesFail(error?.message));
  }
}

function* fetchPharmacyProductsSaga({ payload }) {
  try {
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );
    const response = yield call(searchPharmacyProducts, payload, healthPlanCountryId);
    const data = response?.data?.data || response?.data || {};
    yield put(
      fetchPharmacyProductsSuccess({
        results: data.results || [],
        pagesCount: data.pagesCount || 0,
        totalCount: data.totalCount || 0,
      }),
    );
  } catch (error) {
    yield put(fetchPharmacyProductsFail(error?.message));
  }
}

function* fetchEntitiesSaga({ payload }) {
  try {
    const { data, override } = payload;

    const queryStringOfObj = serializeParams(data);
    const healthPlanCountryId = yield select(
      state => state?.healthPlans?.selectedHealthPlan?.data?.countryId,
    );

    const response = yield call(searchEntities, queryStringOfObj, healthPlanCountryId);

    yield put(fetchEntitiesSuccess({ results: response.data.Data.Results, override }));
  } catch (error) {
    yield put(fetchEntitiesFail(error?.message));
  }
}

function* fetchEntityProvidersSaga({ payload }) {
  try {
    const queryStringOfObj = serializeParams(payload);

    const response = yield call(entityProviders, queryStringOfObj);

    yield put(fetchEntityProvidersSuccess(response.data.data));
  } catch (error) {
    yield put(fetchEntityProvidersFail(error?.message));
  }
}

function* submitEntityProviderSaga({ payload }) {
  try {
    yield call(addEntityProvider, payload);

    yield put(submitEntityProviderSuccess());
    yield put(addToast({ type: 'success', message: 'Entity Provider added successfully' }));
  } catch (error) {
    yield put(submitEntityProviderFail(error?.message));
    yield put(
      addToast({
        type: 'error',
        message: 'An error occured while adding Entity Provider',
      }),
    );
  }
}

function* editEntityProviderSaga({ payload }) {
  try {
    yield call(editEntityProvider, payload);

    yield put(editEntityProviderSuccess());
    yield put(addToast({ type: 'success', message: 'Entity Provider is edited successfully' }));
  } catch (error) {
    yield put(editEntityProviderFail(error?.message));
    yield put(
      addToast({ type: 'error', message: 'An error occured while editing Entity Provider' }),
    );
  }
}

function* removeEntityProviderSaga({ payload }) {
  try {
    yield call(deleteEntityProvider, payload);

    yield put(removeEntityProviderSuccess());
    yield put(addToast({ type: 'success', message: 'Entity Provider is removed successfully' }));
  } catch (error) {
    yield put(removeEntityProviderFail(error?.message));
    yield put(
      addToast({ type: 'error', message: 'An error occured while removing Entity Provider' }),
    );
  }
}

function* healthPlansSaga() {
  yield takeLatest('categories/fetchPriceLists', fetchPriceListsSaga);
  yield takeLatest('categories/fetchRelatedPriceLists', fetchRelatedPriceListsSaga);
  yield takeLatest('categories/submitPriceList', submitPriceListSaga);
  yield takeLatest('categories/editPriceList', editPriceListSaga);
  yield takeLatest('categories/fetchProviders', fetchProvidersSaga);
  yield takeLatest('categories/fetchBranches', fetchBranchesSaga);
  yield takeLatest('categories/fetchDoctorsList', fetchDoctorsSaga);
  yield takeLatest('categories/fetchDoctorProfile', fetchDoctorProfileSaga);
  yield takeLatest('categories/submitProvidersBulk', addProvidersBulkSaga);
  yield takeLatest('categories/fetchPharmaciesList', fetchPharmaciesSaga);
  yield takeLatest('categories/removeProvider', removeProviderSuccessSaga);
  yield takeLatest('categories/editProvider', editProviderSaga);
  yield takeLatest('categories/fetchDropdowns', fetchDropdownsSaga);
  yield takeLatest('categories/fetchServicesList', fetchServicesListSaga);
  yield takeLatest('categories/addService', addServiceSaga);
  yield takeLatest('categories/removeService', removeServiceSaga);
  yield takeLatest('categories/fetchMedicalServices', fetchMedicalServicesSaga);
  yield takeLatest('categories/fetchPharmacyProducts', fetchPharmacyProductsSaga);
  yield takeLatest('categories/fetchEntities', fetchEntitiesSaga);
  yield takeLatest('categories/fetchEntityProviders', fetchEntityProvidersSaga);
  yield takeLatest('categories/submitEntityProvider', submitEntityProviderSaga);
  yield takeLatest('categories/editEntityProvider', editEntityProviderSaga);
  yield takeLatest('categories/removeEntityProvider', removeEntityProviderSaga);
}

export default healthPlansSaga;
